import React, { memo } from 'react';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation } from 'swiper/modules';
import { IoIosArrowBack, IoIosArrowForward } from 'react-icons/io';
import { Loader2 } from 'lucide-react';
import brandMark from '../../assets/feedalogoWhite.svg';
import IntermediateStepsPanel from './ChatSearchSteps/IntermediateStepsPanel';

const REMARK_PLUGINS = [remarkGfm];
const REHYPE_PLUGINS = [rehypeRaw];
const SWIPER_MODULES = [Navigation];

const MARKDOWN_COMPONENTS = {
  table: ({ node, children, ...props }) => (
    <div className="chat-table-wrap">
      <table {...props}>{children}</table>
    </div>
  ),
};

const ChatMessageItem = ({
  elem,
  ind,
  user,
  selectedAgent,
  teamDetails,
  sessionAgentIcon,
  selectedChatSources,
  onSelectSources,
  isLast,
}) => {
  return (
    <div className="min-w-0 max-w-full">
      {elem.human && (
        <div className="heading user-text">
          <div className="icon-box">{user?.first_name?.split('')[0]}</div>
          <strong className="title whitespace-pre-line">{elem?.human}</strong>
          {(elem?.source?.length > 0 || elem?.files?.length > 0) && (
            <button
              className="see-source-button"
              type="button"
              style={{ border: ind === selectedChatSources ? `2px solid var(--primary)` : undefined }}
              onClick={() => onSelectSources(ind)}>
              see sources
            </button>
          )}
        </div>
      )}

      <div className="heading">
        {selectedAgent?.mode === 'team' && elem?.delegate_agent ? (
          <div className="icon-box icon-box--purple">
            <img
              src={
                teamDetails?.members.find(agent => {
                  const normalizedName = agent?.agent_name.toLowerCase().replace(/\s+/g, '-');
                  return normalizedName === elem?.delegate_agent;
                })?.icon ?? brandMark
              }
              alt=""
            />
          </div>
        ) : (
          <div className="icon-box icon-box--purple">
            <img src={sessionAgentIcon ?? brandMark} alt="" />
          </div>
        )}

        {!elem?.delegate_agent && (
          <IntermediateStepsPanel steps={elem?.intermediate_steps} isLoading={elem?.isLoading} />
        )}

        {elem?.isLoading && !elem?.ai && (!elem?.intermediate_steps || elem?.intermediate_steps?.length === 0) && (
          <div className="flex items-center gap-2.5 py-2">
            <Loader2 className="w-4 h-4 animate-spin text-space" />
            <span className="text-sm text-mute">Thinking...</span>
          </div>
        )}

        {elem?.images && elem?.images?.length > 0 && elem?.images !== '' && (
          <div className="swiper-holder">
            <Swiper
              slidesPerView={4.2}
              spaceBetween={12}
              navigation={{
                prevEl: '.swiper-button-prev',
                nextEl: '.swiper-button-next',
              }}
              modules={SWIPER_MODULES}
              breakpoints={{
                320: { slidesPerView: 1.3, spaceBetween: 10 },
                574: { slidesPerView: 2.2, spaceBetween: 12 },
                1250: { slidesPerView: 2.8, spaceBetween: 12 },
                1440: { slidesPerView: 3.2, spaceBetween: 14 },
                1750: { slidesPerView: 4.2, spaceBetween:14 },
              }}>
              {elem?.images?.map((data, index) => (
                <SwiperSlide key={index}>
                  <a href={data?.origin_url} target="_blank" rel="noreferrer" className="img-wrap">
                    <img src={data?.image_url} alt="img" loading="lazy" />
                  </a>
                </SwiperSlide>
              ))}
            </Swiper>
            <div className="swiper-controls">
              <button type="button" className="swiper-button-prev">
                <IoIosArrowBack />
              </button>
              <button type="button" className="swiper-button-next">
                <IoIosArrowForward />
              </button>
            </div>
          </div>
        )}

        {elem.ai && elem.ai !== '' && (
          <div className="ai-message-content">
            <Markdown
              remarkPlugins={REMARK_PLUGINS}
              rehypePlugins={REHYPE_PLUGINS}
              components={MARKDOWN_COMPONENTS}>
              {elem.ai}
            </Markdown>
          </div>
        )}

        {!elem.ai &&
          !elem?.isLoading &&
          (user?.usage?.api_calls >= user?.plan?.request_limit ? (
            <span className="error-text">❌ Limit exceeded. Please upgrade your plan.</span>
          ) : (
            <span className="error-text">Something went wrong !!!</span>
          ))}
      </div>
    </div>
  );
};

export default memo(ChatMessageItem, (prev, next) => {
  if (next.isLast) return false;

  return (
    prev.elem === next.elem &&
    prev.ind === next.ind &&
    prev.selectedChatSources === next.selectedChatSources &&
    prev.sessionAgentIcon === next.sessionAgentIcon &&
    prev.teamDetails === next.teamDetails
  );
});
